import { settingsAtom } from "@framework/shared/atoms";
import { platformFeaturesAtom } from "@framework/shared/atoms/platform-features.atom";
import { trans } from "@mongez/localization";
import {
  BookOpen,
  CheckCircle2,
  Clock3,
  Eye,
  GraduationCap,
  Star,
} from "lucide-react";
import { useLessonHeader } from "../../hooks";
import { LessonLikeButton } from "../LessonLikeButton";

export function LessonHeader() {
  const commentsAndRatingsEnabled =
    platformFeaturesAtom.useValue().commentsAndRatings === true;
  const {
    lesson,
    name,
    image = settingsAtom.get("general")?.logo,
    instructor,
    rating,
    isPaid,
    formattedDuration,
    formattedViews,
    priceText,
    priceSubtext,
  } = useLessonHeader();
  return (
    <section className="overflow-hidden rounded-[2rem] border border-slate-200/80 bg-white shadow-xl shadow-slate-200/40 dark:border-slate-800 dark:bg-slate-950 dark:shadow-black/20">
      <div className="relative aspect-[16/10] min-h-[19rem] overflow-hidden sm:aspect-video">
        <img
          src={image?.url}
          alt={name}
          className="h-full w-full object-cover transition duration-700 hover:scale-[1.02]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/25 to-transparent" />

        <div className="absolute inset-x-0 bottom-0 p-5 text-white sm:p-8">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            {lesson.subject?.name && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-xs font-bold backdrop-blur-md">
                <BookOpen className="h-3.5 w-3.5" />
                {lesson.subject.name}
              </span>
            )}
            {lesson.grade?.name && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-xs font-bold backdrop-blur-md">
                <GraduationCap className="h-3.5 w-3.5" />
                {lesson.grade.name}
              </span>
            )}
            {isPaid && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1.5 text-xs font-black shadow-lg shadow-emerald-950/20">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {trans("grades.lessonPurchased")}
              </span>
            )}
          </div>

          <h1 className="max-w-4xl text-2xl font-black leading-tight drop-shadow-sm sm:text-4xl">
            {name}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-slate-100">
            <span className="inline-flex items-center gap-1.5">
              <Eye className="h-4 w-4" />
              {formattedViews}
            </span>
            {commentsAndRatingsEnabled ? (
              <span className="inline-flex items-center gap-1.5">
                <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                <span className="font-bold">{rating || 0}</span>
              </span>
            ) : null}
            {formattedDuration && (
              <span className="inline-flex items-center gap-1.5">
                <Clock3 className="h-4 w-4" />
                {formattedDuration}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 border-t border-slate-100 p-5 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <div className="flex items-center gap-3">
          {instructor?.image?.url ? (
            <img
              src={instructor.image.url}
              alt={instructor.name}
              className="h-12 w-12 rounded-2xl border-2 border-white object-cover shadow-md dark:border-slate-800"
            />
          ) : (
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <GraduationCap className="h-6 w-6" />
            </span>
          )}
          <div>
            <p className="text-xs font-bold text-slate-500 dark:text-slate-400">
              {trans("grades.yourInstructor")}
            </p>
            <h2 className="mt-0.5 font-black text-slate-950 dark:text-white">
              {instructor?.name}
            </h2>
            {instructor?.label && (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {instructor.label}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <LessonLikeButton lesson={lesson} />
          {!isPaid && (
            <div className="rounded-2xl bg-slate-50 px-5 py-3 dark:bg-slate-900 sm:text-end">
              <p className="text-xl font-black text-slate-950 dark:text-white">
                {priceText}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {priceSubtext}
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
